import { formatDate } from '@comforthub/design-system'
import { api } from './api'
import type { Persona } from './personas'
import { sinceLabel } from './storePrinter'

/**
 * Devices, phase 1: the phones that run TOU Field and the print stations next
 * to a store's receipt printer, as service-core reports them.
 *
 * Merchant (owner or manager of the active company): GET /api/devices,
 * GET|PATCH /api/devices/{id}, POST /api/devices/{id}/test-alarm and
 * POST /api/devices/{id}/revoke. Platform admin: the same under
 * /api/admin/devices, across every company and kind, plus a version summary
 * and the masked push token. admin:users:read to see, admin:users:write to act.
 *
 * Phase 2: a store printer is two devices, the phone next to it (kind
 * print_station, "Print phone") and the receipt printer itself (kind printer,
 * id = the store printer's id), listed right under its phone. The phone says
 * whether it is online; the printer whether it answers.
 *
 * Every piece of text a device shows is decided here, so the table, the drawer
 * and the store's Devices card say the same thing.
 */

export type DeviceKind = 'print_station' | 'printer' | 'shop_device' | 'staff_phone' | 'courier_phone' | 'client_device'
export type DeviceProblem = 'alarms' | 'printing' | 'stale' | 'oldVersion'
export type AlarmReason =
  | 'notifications_off'
  | 'alarm_channel_off'
  | 'alarm_channel_low'
  | 'battery_optimised'
  | 'old_version'
  | 'not_seen_24h'

/** The merchant list or the platform admin's. */
export type DeviceScope = 'merchant' | 'admin'

export interface DeviceBattery {
  level?: number | null
  charging?: boolean | null
  lowPower?: boolean | null
  optimised?: boolean | null
}

export interface DeviceNotifications {
  permission?: string | null
  alarmChannel?: string | null
}

export interface DeviceLocation {
  foreground?: string | null
  background?: string | null
}

export interface DeviceNetwork {
  type?: string | null
  wifiName?: string | null
  signal?: number | null
  ip?: string | null
  /** Mbps; print stations. */
  linkSpeed?: number | null
  /** Print stations. */
  gateway?: string | null
}

/** Ready / not answering as the phone last saw it; unknown while the phone is offline. */
export type PrinterState = 'ready' | 'not_answering' | 'unknown'

export interface PrinterIdentity {
  maker?: string | null
  model?: string | null
  serial?: string | null
}

/**
 * A print phone's printer, or a printer row's own report. Everything after
 * port is on printer rows only.
 */
export interface DevicePrinter {
  /** The printer's name on the store, else its model. */
  name?: string | null
  reachable?: boolean | null
  state?: PrinterState | string | null
  /** As the phone reports it: "192.168.8.101:9100". */
  address?: string | null
  host?: string | null
  port?: number | null
  identity?: PrinterIdentity | null
  autoFind?: boolean | null
  lastPrintedAt?: string | null
  lastError?: string | null
  /** Jobs queued or on their way to the phone. */
  queued?: number | null
  /** Jobs that failed for good in the last 24 hours. */
  failed24h?: number | null
}

export interface DeviceView {
  id: string
  kind: DeviceKind | string
  /** The label, else the model, else the printer's name. */
  name?: string | null
  label?: string | null
  platform?: string | null
  model?: string | null
  osVersion?: string | null
  /** Field app: the store build. Print station: the print agent's version. */
  nativeVersion?: string | null
  otaUpdateId?: string | null
  channel?: string | null
  runtimeVersion?: string | null
  /** Below the latest field app (or print agent) version. */
  oldVersion?: boolean | null
  person?: { userId: string; name?: string | null; email?: string | null } | null
  companies?: { id: string; name: string }[] | null
  store?: { id: string; name: string } | null
  printerId?: string | null
  firstSeenAt?: string | null
  lastSeenAt?: string | null
  /** Not seen for 7 days, or never. */
  stale?: boolean | null
  revokedAt?: string | null
  /** Null is unknown, and always null for print stations. */
  canReceiveAlarms?: boolean | null
  reasons?: (AlarmReason | string)[] | null
  alarmSound?: string | null
  hasPushToken?: boolean | null
  /** Platform admin only, masked: "ExponentPushToken[abcd…wxyz]". Always null for merchants. */
  pushToken?: string | null
  battery?: DeviceBattery | null
  notifications?: DeviceNotifications | null
  location?: DeviceLocation | null
  network?: DeviceNetwork | null
  printer?: DevicePrinter | null
  /** Print phone: heard from in the last 90 s (the Printer card's rule). */
  online?: boolean | null
  /** Print phone: when it was last heard from, while it is offline. */
  offlineSince?: string | null
  /** Print phone: its printer's id (the printer row). */
  printerDeviceId?: string | null
  /** Printer: its phone's id, null while the phone has never reported. */
  phoneDeviceId?: string | null
  /** Print phone: installs a new print app by itself. */
  autoUpdate?: boolean | null
  /** Print phone: the server ships another print app than it runs. */
  updateAvailable?: boolean | null
}

export interface DeviceVersionCount {
  kind: DeviceKind | string
  nativeVersion?: string | null
  channel?: string | null
  count: number
}

export interface DeviceList {
  items: DeviceView[]
  total: number
  latest?: { fieldApp?: string | null; printAgent?: string | null } | null
  /** Platform admin only. */
  versions?: DeviceVersionCount[] | null
}

/** The list's filters, as they sit in the page's URL. Empty means any. */
export interface DeviceFilters {
  kind?: string
  storeId?: string
  problem?: string
  /** Platform admin only. */
  companyId?: string
}

export const DEVICE_FILTER_KEYS = ['kind', 'storeId', 'problem', 'companyId'] as const

// ── Words ────────────────────────────────────────────────────────────────

export const KIND_LABELS: Record<DeviceKind, string> = {
  print_station: 'Print phone',
  printer: 'Receipt printer',
  shop_device: 'Shop device',
  staff_phone: 'Staff phone',
  courier_phone: 'Courier phone',
  client_device: 'Client device',
}

/** What a merchant's list can hold: the API never sends a merchant anything else. */
export const MERCHANT_KINDS: DeviceKind[] = ['staff_phone', 'print_station', 'printer']
export const ALL_KINDS: DeviceKind[] = [
  'staff_phone',
  'courier_phone',
  'print_station',
  'printer',
  'shop_device',
  'client_device',
]

export const PROBLEM_LABELS: Record<DeviceProblem, string> = {
  alarms: "Can't receive alarms",
  printing: "Can't print",
  stale: 'Not seen for 7 days',
  oldVersion: 'Old app version',
}
export const PROBLEMS: DeviceProblem[] = ['alarms', 'printing', 'stale', 'oldVersion']

export const REASON_TEXT: Record<AlarmReason, string> = {
  notifications_off: 'Notifications are off',
  alarm_channel_off: 'New-order alarm is turned off',
  alarm_channel_low: 'New-order alarm is set to silent/low',
  battery_optimised: 'Battery optimisation is on',
  old_version: 'App is older than 1.0.2 — update from the store',
  not_seen_24h: 'Not seen in the last 24 hours',
}

/** The text for a reason code; a code this build doesn't know reads as itself, in words. */
export function reasonText(code: string): string {
  if (code in REASON_TEXT) return REASON_TEXT[code as AlarmReason]
  const words = code.replace(/_/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : code
}

export function kindLabel(kind: string | null | undefined): string {
  if (!kind) return '—'
  return KIND_LABELS[kind as DeviceKind] ?? reasonText(kind)
}

export const EMPTY_LIST_TEXT = 'Devices appear here when staff open TOU Field or a store connects a printer'

/**
 * Owners and managers see the company's devices; service-core answers a worker
 * 403. The API decides by company role, not by a permission code, so the menu
 * and the route go by the active persona, as the rest of the merchant menu does.
 */
export function canManageDevices(activePersona: Persona | 'None' | null | undefined): boolean {
  return activePersona === 'Merchant' || activePersona === 'Manager'
}

/** Phones that run TOU Field: the only devices that take a test alarm or can be signed out. */
export function isFieldAppDevice(device: Pick<DeviceView, 'kind'>): boolean {
  return device.kind === 'staff_phone' || device.kind === 'courier_phone'
}

export const isPrintPhone = (device: Pick<DeviceView, 'kind'>) => device.kind === 'print_station'
export const isPrinterDevice = (device: Pick<DeviceView, 'kind'>) => device.kind === 'printer'

/** A print phone and its printer go by their kind when they have no name or model; anything else is "Unnamed …". */
export function deviceName(device: Pick<DeviceView, 'name' | 'kind'>): string {
  const name = device.name?.trim()
  if (name) return name
  if (isPrintPhone(device) || isPrinterDevice(device)) return kindLabel(device.kind)
  return `Unnamed ${kindLabel(device.kind).toLowerCase()}`
}

export type DeviceStatusTone = 'ok' | 'problem' | 'unknown'

export interface DeviceStatus {
  text: string
  tone: DeviceStatusTone
}

/**
 * The Status column of a print phone ("Online", "Offline since 14:05") or a
 * printer ("Ready", "Not answering", "Unknown — phone offline"). Null for
 * every other kind: a phone's status is whether it can ring (Alarms OK).
 */
export function deviceStatus(
  device: Pick<DeviceView, 'kind' | 'online' | 'offlineSince' | 'printer'>,
  now: number = Date.now(),
): DeviceStatus | null {
  if (isPrintPhone(device)) {
    // A service-core before devices phase 2 doesn't say: no status rather than a wrong "Offline".
    if (device.online == null) return null
    if (device.online) return { text: 'Online', tone: 'ok' }
    return {
      text: device.offlineSince ? `Offline since ${sinceLabel(device.offlineSince, now)}` : 'Offline',
      tone: 'problem',
    }
  }
  if (isPrinterDevice(device)) {
    switch (device.printer?.state) {
      case 'ready':
        return { text: 'Ready', tone: 'ok' }
      case 'not_answering':
        return { text: 'Not answering', tone: 'problem' }
      default:
        return { text: 'Unknown — phone offline', tone: 'unknown' }
    }
  }
  return null
}

/**
 * The ids of printer rows that sit right under their own phone in `items`,
 * so the list can indent them. A printer whose phone isn't the row above it
 * (filtered out, or never reported) stands on its own.
 */
export function nestedPrinterIds(items: Pick<DeviceView, 'id' | 'kind' | 'phoneDeviceId'>[]): Set<string> {
  const nested = new Set<string>()
  items.forEach((d, i) => {
    if (isPrinterDevice(d) && d.phoneDeviceId && i > 0 && items[i - 1].id === d.phoneDeviceId) nested.add(d.id)
  })
  return nested
}

/** "EPSON TM-T88III · ZY2205AB0001", or null when the printer hasn't said who it is. */
export function printerIdentityText(identity: PrinterIdentity | null | undefined): string | null {
  const name = [identity?.maker?.trim(), identity?.model?.trim()].filter(Boolean).join(' ')
  const parts = [name, identity?.serial?.trim()].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

/** Who carries a phone, or where a print station stands. */
export function personOrStore(device: Pick<DeviceView, 'person' | 'store'>): string {
  const person = device.person?.name?.trim() || device.person?.email?.trim()
  if (person) return person
  return device.store?.name?.trim() || '—'
}

/** The person the confirm text names: who will be signed out, else the device. */
function signOutWho(device: Pick<DeviceView, 'person' | 'name' | 'kind'>): string {
  return device.person?.name?.trim() || device.person?.email?.trim() || deviceName(device)
}

export function signOutConfirmText(device: Pick<DeviceView, 'person' | 'name' | 'kind'>): string {
  return `${signOutWho(device)} will be signed out of TOU Field on this phone, for every company. They can sign in again.`
}

export function companiesText(device: Pick<DeviceView, 'companies'>): string {
  const names = (device.companies ?? []).map((c) => c.name?.trim()).filter(Boolean)
  return names.length ? names.join(', ') : '—'
}

export type AlarmState = 'ok' | 'problem' | 'unknown'

export function alarmState(device: Pick<DeviceView, 'canReceiveAlarms'>): AlarmState {
  if (device.canReceiveAlarms === true) return 'ok'
  if (device.canReceiveAlarms === false) return 'problem'
  return 'unknown'
}

export function alarmReasons(device: Pick<DeviceView, 'reasons'>): string[] {
  return (device.reasons ?? []).map(reasonText)
}

/** A printer's network is its address on the shop Wi-Fi; any other device's, its own connection. */
export function deviceNetworkLabel(device: Pick<DeviceView, 'kind' | 'network' | 'printer'>): string {
  if (isPrinterDevice(device)) return device.printer?.address?.trim() || '—'
  return networkLabel(device.network)
}

/** "Office Wi-Fi", "Wi-Fi", "Mobile data", or "—". */
export function networkLabel(network: DeviceNetwork | null | undefined): string {
  if (!network) return '—'
  if (network.type === 'cellular') return 'Mobile data'
  if (network.type === 'wifi') return network.wifiName?.trim() || 'Wi-Fi'
  if (network.wifiName?.trim()) return network.wifiName.trim()
  if (network.type === 'none') return 'No connection'
  return '—'
}

/** 0.82 → "82%"; null when the level is unknown. */
export function batteryPercent(battery: DeviceBattery | null | undefined): string | null {
  const level = battery?.level
  if (level == null || !Number.isFinite(level)) return null
  // The app sends 0–1; a level already in percent is left as it is.
  const percent = level <= 1 ? level * 100 : level
  return `${Math.round(Math.max(0, Math.min(100, percent)))}%`
}

/** "just now", "12 min ago", "3 h ago", "yesterday", "4 days ago", then the date. Null is "Never". */
export function lastSeenText(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return 'Never'
  const at = new Date(iso).getTime()
  if (!Number.isFinite(at)) return '—'
  const minutes = Math.floor((now - at) / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`
  return formatDate(iso)
}

const PLATFORM_LABELS: Record<string, string> = { ios: 'iOS', android: 'Android' }
const PERMISSION_LABELS: Record<string, string> = {
  granted: 'Allowed',
  denied: 'Not allowed',
  undetermined: 'Not asked yet',
}
const CHANNEL_LABELS: Record<string, string> = {
  max: 'Loudest (max)',
  high: 'Loud (high)',
  default: 'Default',
  low: 'Silent / low',
  none: 'Turned off',
  blocked: 'Blocked',
}
const SOUND_LABELS: Record<string, string> = {
  chime: 'Chime',
  bell: 'Bell',
  siren: 'Siren',
  phone: 'Phone ringtone',
  off: 'Off',
}
const NETWORK_TYPE_LABELS: Record<string, string> = {
  wifi: 'Wi-Fi',
  cellular: 'Mobile data',
  none: 'No connection',
  other: 'Other',
}

const lookup = (table: Record<string, string>, value: string | null | undefined): string =>
  value ? table[value] ?? value : '—'

export const platformLabel = (value?: string | null) => lookup(PLATFORM_LABELS, value)
export const permissionLabel = (value?: string | null) => lookup(PERMISSION_LABELS, value)
export const alarmChannelLabel = (value?: string | null) => lookup(CHANNEL_LABELS, value)
export const alarmSoundLabel = (value?: string | null) => lookup(SOUND_LABELS, value)
export const networkTypeLabel = (value?: string | null) => lookup(NETWORK_TYPE_LABELS, value)

/** true → "Yes", false → "No", unknown → "—". */
export function yesNo(value: boolean | null | undefined, yes = 'Yes', no = 'No'): string {
  if (value === true) return yes
  if (value === false) return no
  return '—'
}

/**
 * The version strip: one entry per kind × version × channel, phones first,
 * newest version first within a kind.
 */
export function sortedVersions(versions: DeviceVersionCount[] | null | undefined): DeviceVersionCount[] {
  const kindOrder = (kind: string) => {
    const i = ALL_KINDS.indexOf(kind as DeviceKind)
    return i === -1 ? ALL_KINDS.length : i
  }
  return [...(versions ?? [])].sort(
    (a, b) =>
      kindOrder(a.kind) - kindOrder(b.kind) ||
      (b.nativeVersion ?? '').localeCompare(a.nativeVersion ?? '', undefined, { numeric: true }) ||
      (a.channel ?? '').localeCompare(b.channel ?? ''),
  )
}

// ── Requests ─────────────────────────────────────────────────────────────

export const deviceBase = (scope: DeviceScope) => (scope === 'admin' ? '/admin/devices' : '/devices')

/** The filters the API takes, without empty ones; a merchant never sends companyId. */
export function deviceListParams(filters: DeviceFilters, scope: DeviceScope): Record<string, string> {
  const params: Record<string, string> = {}
  for (const key of DEVICE_FILTER_KEYS) {
    if (key === 'companyId' && scope !== 'admin') continue
    const value = filters[key]?.trim()
    if (value) params[key] = value
  }
  return params
}

export const deviceKeys = {
  all: ['devices'] as const,
  scope: (scope: DeviceScope) => ['devices', scope] as const,
  list: (scope: DeviceScope, params: Record<string, string>) => ['devices', scope, 'list', params] as const,
  detail: (scope: DeviceScope, id: string) => ['devices', scope, 'detail', id] as const,
}

export async function fetchDevices(scope: DeviceScope, params: Record<string, string>): Promise<DeviceList> {
  const { data } = await api.get<DeviceList>(deviceBase(scope), { params })
  return { ...data, items: Array.isArray(data?.items) ? data.items : [], total: data?.total ?? 0 }
}

export async function fetchDevice(scope: DeviceScope, id: string): Promise<DeviceView> {
  return (await api.get<DeviceView>(`${deviceBase(scope)}/${encodeURIComponent(id)}`)).data
}

/** A blank label clears it, and the list shows the model again. */
export async function renameDevice(scope: DeviceScope, id: string, label: string): Promise<DeviceView> {
  const trimmed = label.trim()
  return (
    await api.patch<DeviceView>(`${deviceBase(scope)}/${encodeURIComponent(id)}`, { label: trimmed ? trimmed : null })
  ).data
}

export async function sendTestAlarm(scope: DeviceScope, id: string): Promise<void> {
  await api.post(`${deviceBase(scope)}/${encodeURIComponent(id)}/test-alarm`)
}

export async function revokeDevice(scope: DeviceScope, id: string): Promise<DeviceView> {
  return (await api.post<DeviceView>(`${deviceBase(scope)}/${encodeURIComponent(id)}/revoke`)).data
}

/** A label is at most this long (the API refuses more). */
export const LABEL_MAX = 80
